import { Action, createSlice, createAsyncThunk, isAnyOf } from "@reduxjs/toolkit";
import { smsApi } from "api/instances";
import { GlobalApplicationState } from "globalApplicationState";
import { ISMS, ISMSFilters, ISMSListing, ISMSSentConfirmation, ISMSState, SMSStatus } from "./models";

export interface ISetSMSListAction extends Action {
    payload: ISMSListing;
}

const DEFAULT_SMS_LIST_STATE = {
    currentPage: 0,
    totalSMS: 0,
    totalPages: 0,
    sms: [],
    isFetching: false
}

export const DEFAULT_SMS: ISMS = {
    id: "",
    title: "",
    tenantId: "",
    sentBy: undefined,
    sentTime: undefined,
    plainBody: "",
    htmlBody: "",
    status: SMSStatus.Draft,
    audiences: [],
    sendToAllUsers: false,
    fromSender: {
        name: "",
        email: ""
    },
    fromNumber: process.env.REACT_APP_DEFAULT_SMS_NUMBER //Currently we only have one number we text from.
}

const DEFAULT_SMS_STATE: ISMSState = {
    draftSMSList: { id: SMSStatus.Draft, ...DEFAULT_SMS_LIST_STATE },
    scheduledSMSList: { id: SMSStatus.Scheduled, ...DEFAULT_SMS_LIST_STATE },
    sentSMSList: { id: SMSStatus.Sent, ...DEFAULT_SMS_LIST_STATE },
    editor: {
        sms: DEFAULT_SMS,
        originalSms: DEFAULT_SMS,
        changedSinceLastSaved: false,
        isFetching: false,
        isSaving: false,
        testNumbers: [],
        shouldDownloadRecipientsList: false,
        shouldSendTestSMS: false,
        shouldOpenOneLastCheck: false,
    },
    successMessage: "",
    errorMessage: "",
    sentConfirmation: {
        shouldShowDialog: false,
        expectedSentTime: "",
        smsId: ""
    }
}

export interface IUpdateSMSEditorAction extends Action {
    payload: {
        sms: Partial<ISMS>;
        changedSinceLastSaved?: boolean;
    };
}

export interface IUpdateOrigninalSMSEditorAction extends Action {
    payload: {
        originalSms: Partial<ISMS>;
    };
}

export interface ISetSMSEditorAction extends Action {
    payload: {
        sms: ISMS;
    };
}

export interface ICreateNewSMSThunkPayload {
    redirectUrl?: string;
}

export interface IGetSMSToEditThunkPayload {
    smsId: string;
}

export interface IGetSMSAudienceTotalsPayload {
    audienceIds: string[];
}

export interface ISetTestSMSAction extends Action {
    payload: {
        testNumbers: string[];
    };
}

export interface ISetRedirectUrlAction extends Action {
    payload: {
        redirectUrl?: string;
    };
}

export interface ISetShouldDownloadAction extends Action {
    payload: {
        shouldDownload: boolean;
    };
}

export interface ISetShouldSendAction extends Action {
    payload: {
        shouldSend: boolean;
    };
}

export interface ISetShouldOpenAction extends Action {
    payload: {
        shouldOpen: boolean;
    };
}

export interface ISetShouldShowSentConfirmAction extends Action {
    payload: {
        sentConfirmation: ISMSSentConfirmation;
    }
}

export interface IChangedSinceLastSavedEditorAction extends Action {
    payload: {
        changedSinceLastSaved: boolean;
    };
}

export interface IGetSMSListThunkPayload {
    pageAmount: number;
    pageNumber: number;
    filters: ISMSFilters;
}

export const getDraftSMSList = createAsyncThunk<void, IGetSMSListThunkPayload, { state: GlobalApplicationState }>(
    "GET_DRAFT_SMS_POST_LIST",
    async (payload: IGetSMSListThunkPayload, { dispatch }) => {
        try {
            const { data } = 
                await smsApi.getSMSList(payload.pageAmount, payload.pageNumber, {...payload.filters, status: SMSStatus.Draft});

            dispatch({ type: smsSlice.actions.SET_DRAFT_SMS_LIST, payload: data});
        } catch (err) {
            throw err;
        }
    }
);

export const getScheduledSMSList = createAsyncThunk<void, IGetSMSListThunkPayload, { state: GlobalApplicationState }>(
    "GET_SCHEDULED_SMS_POST_LIST",
    async (payload: IGetSMSListThunkPayload, { dispatch }) => {
        try {
            const { data } = 
                await smsApi.getSMSList(payload.pageAmount, payload.pageNumber, {...payload.filters, status: SMSStatus.Scheduled});

            dispatch({ type: smsSlice.actions.SET_SCHEDULED_SMS_LIST, payload: data});
        } catch (err) {
            throw err;
        }
    }
);

export const getSentSMSList = createAsyncThunk<void, IGetSMSListThunkPayload, { state: GlobalApplicationState }>(
    "GET_SENT_SMS_POST_LIST",
    async (payload: IGetSMSListThunkPayload, { dispatch }) => {
        try {
            const { data } = 
                await smsApi.getSMSList(payload.pageAmount, payload.pageNumber, {...payload.filters, status: SMSStatus.Sent});

            dispatch({ type: smsSlice.actions.SET_SENT_SMS_LIST, payload: data});
        } catch (err) {
            throw err;
        }
    }
);

export const createNewSMS = createAsyncThunk<void, ICreateNewSMSThunkPayload, { state: GlobalApplicationState }>(
    "CREATE_NEW_SMS",
    async (payload: ICreateNewSMSThunkPayload, { dispatch, getState }) => {
        try {
            const { data } = await smsApi.createNewSMS(getState().sms.editor.sms);

            dispatch({ type: smsSlice.actions.SET_REDIRECT_URL, payload: { redirectUrl: payload.redirectUrl } });
            dispatch({ type: smsSlice.actions.SET_SMS_EDITOR, payload: { sms: data }});
        } catch (err) {
            throw err;
        }
    }
);

export const saveExistingSMS = createAsyncThunk<void, ICreateNewSMSThunkPayload, { state: GlobalApplicationState }>(
    "SAVE_EXISTING_SMS",
    async (payload: ICreateNewSMSThunkPayload, { dispatch, getState }) => {
        try {
            await smsApi.saveExistingSMS(getState().sms.editor.sms);

            dispatch({ type: smsSlice.actions.SET_REDIRECT_URL, payload: { redirectUrl: payload.redirectUrl } });
            dispatch({ type: smsSlice.actions.SET_CHANGED_SINCE_LAST_SAVED, payload: { changedSinceLastSaved: false } });
            dispatch({ type: smsSlice.actions.SET_SMS_EDITOR, payload: { sms: getState().sms.editor.sms } });
        } catch (err) {
            throw err;
        }
    }
);

export const getSMSToEdit = createAsyncThunk<void, IGetSMSToEditThunkPayload, { state: GlobalApplicationState }>(
    "GET_SMS_TO_EDIT",
    async (payload: IGetSMSToEditThunkPayload, { dispatch }) => {
        try {
            const { data } = await smsApi.getSMS(payload.smsId);
            
            dispatch({ type: smsSlice.actions.SET_SMS_EDITOR, payload: { sms: data }});
        } catch (err) {
            throw err;
        }
    }
);

export const smsSlice = createSlice({
    name: "sms",
    initialState: DEFAULT_SMS_STATE,
    reducers: {
        SET_DRAFT_SMS_LIST: (state: ISMSState, action: ISetSMSListAction) => {
            state.draftSMSList = {
                ...action.payload,
                id: SMSStatus.Draft,
                isFetching: false
            }
        },
        SET_SCHEDULED_SMS_LIST: (state: ISMSState, action: ISetSMSListAction) => {
            state.scheduledSMSList = {
                ...action.payload,
                id: SMSStatus.Scheduled,
                isFetching: false
            }
        },
        SET_SENT_SMS_LIST: (state: ISMSState, action: ISetSMSListAction) => {
            state.sentSMSList = {
                ...action.payload,
                id: SMSStatus.Sent,
                isFetching: false
            }
        },
        UPDATE_SMS_EDITOR: (state: ISMSState, action: IUpdateSMSEditorAction) => {
            state.editor.sms = { ...state.editor.sms, ...action.payload.sms };
            state.editor.changedSinceLastSaved = action.payload.changedSinceLastSaved ?? state.editor.changedSinceLastSaved;
        },
        UPDATE_ORIGINAL_SMS_EDITOR: (state: ISMSState, action: IUpdateOrigninalSMSEditorAction) => {
            state.editor.originalSms = { ...state.editor.originalSms, ...action.payload.originalSms };
        },
        SET_SMS_EDITOR: (state: ISMSState, action: ISetSMSEditorAction) => {
            state.editor.originalSms = action.payload.sms;
            state.editor.sms = action.payload.sms;
            state.editor.changedSinceLastSaved = false;
        },
        SET_SHOULD_DOWNLOAD_RECIPIENTS_LIST: (state: ISMSState, action: ISetShouldDownloadAction) => {
            state.editor.shouldDownloadRecipientsList = action.payload.shouldDownload;
        },
        SET_SHOULD_SEND_TEST_SMS: (state: ISMSState, action: ISetShouldSendAction) => {
            state.editor.shouldSendTestSMS = action.payload.shouldSend;
        },
        SET_TEST_SMS: (state: ISMSState, action: ISetTestSMSAction) => {
            state.editor.testNumbers = action.payload.testNumbers;
        },
        SET_SHOULD_OPEN_ONE_LAST_CHECK: (state: ISMSState, action: ISetShouldOpenAction) => {
            state.editor.shouldOpenOneLastCheck = action.payload.shouldOpen;
        },
        SET_SHOW_SENT_CONFIRMATION: (state: ISMSState, action: ISetShouldShowSentConfirmAction) => {
            state.sentConfirmation = action.payload.sentConfirmation;
        },
        SET_REDIRECT_URL: (state: ISMSState, action: ISetRedirectUrlAction) => {
            state.redirectUrl = action.payload.redirectUrl;
        },
        SET_CHANGED_SINCE_LAST_SAVED: (state: ISMSState, action: IChangedSinceLastSavedEditorAction) => {
            state.editor.changedSinceLastSaved = action.payload.changedSinceLastSaved;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getDraftSMSList.pending, (state: ISMSState) => {
                state.draftSMSList.isFetching = true;
            })
            .addCase(getDraftSMSList.fulfilled, (state: ISMSState) => {
                state.draftSMSList.isFetching = false;
            })
            .addCase(getDraftSMSList.rejected, (state: ISMSState) => {
                state.draftSMSList.isFetching = false;
            })
            .addCase(getScheduledSMSList.pending, (state: ISMSState) => {
                state.scheduledSMSList.isFetching = true;
            })
            .addCase(getScheduledSMSList.fulfilled, (state: ISMSState) => {
                state.scheduledSMSList.isFetching = false;
            })
            .addCase(getScheduledSMSList.rejected, (state: ISMSState) => {
                state.scheduledSMSList.isFetching = false;
            })
            .addCase(getSentSMSList.pending, (state: ISMSState) => {
                state.sentSMSList.isFetching = true;
            })
            .addCase(getSentSMSList.fulfilled, (state: ISMSState) => {
                state.sentSMSList.isFetching = false;
            })
            .addCase(getSentSMSList.rejected, (state: ISMSState) => {
                state.sentSMSList.isFetching = false;
            })
            .addCase(getSMSToEdit.pending, (state: ISMSState) => {
                state.editor.isFetching = true;
            })
            .addCase(getSMSToEdit.fulfilled, (state: ISMSState) => {
                state.editor.isFetching = false;
            })
            .addCase(getSMSToEdit.rejected, (state: ISMSState) => {
                state.editor.isFetching = false;
            })
            .addMatcher(isAnyOf(createNewSMS.pending, saveExistingSMS.pending), (state: ISMSState) => {
                state.editor.isSaving = true;
            })
            .addMatcher(isAnyOf(createNewSMS.fulfilled, saveExistingSMS.fulfilled), (state: ISMSState) => {
                state.editor.isSaving = false;
            })
            .addMatcher(isAnyOf(createNewSMS.rejected, saveExistingSMS.rejected), (state: ISMSState) => {
                state.editor.isSaving = false;
            });
    }
});